// Shared GLB/glTF loader + cache. Each source file is fetched and parsed ONCE
// (promise-cached), then cloned per scene/instance. This is the key to running
// four simultaneous WebGL canvases that all draw the same car/building/tree
// models without re-downloading or re-parsing.

import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js'
import { clone as skeletonClone } from 'three/addons/utils/SkeletonUtils.js'
import * as THREE from 'three'

const loader = new GLTFLoader()
const cache = new Map()   // url -> Promise<{ scene, animations }>

// Asset host base. In dev, load from the Express backend (Vite doesn't proxy
// /assets). In prod, relative (Express/Render serves them) unless VITE_ASSET_BASE
// points elsewhere — e.g. a Bunny CDN pull zone mirroring the same /assets/... paths.
const ASSET_BASE = import.meta.env.VITE_ASSET_BASE
  || (import.meta.env.DEV ? `${location.protocol}//${location.hostname}:8787` : '')
const A = `${ASSET_BASE}/assets`
export const URLS = {
  car: (name) => `${A}/cars_glb/models_glb/${name}.glb`,
  city: (name) => `${A}/City_gltf/${encodeURI('Exports/glTF (Godot)')}/${name}.gltf`,
  nature: (name) => `${A}/Nature_glTF/${name}.gltf`,
  animal: (name) => `${A}/Animals_gltf/${name}.gltf`
}

export function loadGLTF(url) {
  if (cache.has(url)) return cache.get(url)
  const p = new Promise((resolve, reject) => {
    loader.load(url,
      (gltf) => {
        // Normalize: ensure shadows/material sides are sane and frustum culling on.
        gltf.scene.traverse((o) => {
          if (o.isMesh) { o.castShadow = false; o.receiveShadow = false; o.frustumCulled = true }
        })
        resolve({ scene: gltf.scene, animations: gltf.animations || [] })
      },
      undefined,
      (err) => { console.error('[assetLoader] failed', url, err?.message || err); reject(err) }
    )
  })
  cache.set(url, p)
  return p
}

// Return a fresh clone of a cached model. Use skeleton-aware clone for rigged/
// animated models (animals, characters), plain clone for static props.
export async function instance(url, { rigged = false } = {}) {
  const { scene, animations } = await loadGLTF(url)
  const obj = rigged ? skeletonClone(scene) : scene.clone(true)
  return { obj, animations }
}

// Compute a model's bounding box size once (for tiling/placement math).
export async function modelSize(url) {
  const { scene } = await loadGLTF(url)
  const box = new THREE.Box3().setFromObject(scene)
  const size = new THREE.Vector3()
  box.getSize(size)
  return size
}

// Fire-and-forget warm cache for a list of urls (parallel). Resolves when all
// settle; individual failures don't reject the batch.
export function preload(urls) {
  return Promise.allSettled(urls.map(loadGLTF))
}
